import _ from "lodash-es";
import {ucFirst} from "@/lib/import/Util";
import {computeCompareValueOperatorString, durationUnitToText} from "@/src/utils/attributeUtils";
import {isAny} from "@/lib/features/modeling/util/ModelingUtil";
import {getBBox} from "diagram-js/lib/util/Elements";

export const DEFAULT_ACTIVITY_SIZE = {
    width: 150,
    height: 90
}

export const DEFAULT_NESTING_ACTIVITY_SIZE = {
    width: 500,
    height: 300
}

/**
 * Is an element of the given od type?
 *
 * @param  {djs.model.Base|ModdleElement} element
 * @param  {String} type
 *
 * @return {Boolean}
 */
export function is(element, type) {
    const bo = getBusinessObject(element);

    return bo && (typeof bo.$instanceOf === 'function') && bo.$instanceOf(type);
}

/**
 * check if the element is a connection type
 * @param element
 * @returns {boolean}
 */
export function isLink(element) {
    const bo = getBusinessObject(element);

    return bo && (typeof bo.$instanceOf === 'function') &&
        bo.$instanceOf('od:Link')
        && element.type !== 'label'; //the business object of link label is still a link so added this condition to avoid
}

/**
 * Return the business object for a given element.
 *
 * @param  {djs.model.Base|ModdleElement} element
 *
 * @return {ModdleElement}
 */
export function getBusinessObject(element) {
    return (element && element.businessObject) || element;
}

/**
 * return property of the business object of an element
 * @param element
 * @param property
 * @returns {*}
 */
export function getProperty(element, property) {
    element = getBusinessObject(element);
    return element && element[property];
}

/**
 * when a property is an object, this methods allows to get the nested property value
 * @param element
 * @param externalProp
 * @param internalProp
 * @returns {*}
 */
export function getDeepProperty(element, externalProp, internalProp) {
    const ext = getProperty(element, externalProp),
        defaultExt = getDefaultProperty(element, externalProp);

    if (ext && ext[internalProp]) {
        return ext[internalProp];
    }

    return defaultExt && defaultExt[internalProp];
}

/**
 * return default value  of a deep property
 * @param element
 * @param property
 * @returns
 */
export function getDefaultProperty(element, property) {
    switch (property) {
        case 'states':
            return getDefaultStates(element);
        case 'expression':
            return getDefaultExpression(element);
        case 'duration':
            return getDefaultDuration(element);
        case 'roles':
            return getDefaultRoles(element);
    }
}

/**
 * default value of states property
 * @param element
 * @returns {{excluded: boolean, pending: boolean, executed: boolean}}
 */
export function getDefaultStates(element) {
    return {
        excluded: false,
        pending: false,
        executed: false
    }
}

/**
 * default value of duration property
 * @param element
 * @returns {{unit: string, value: string}}
 */
export function getDefaultDuration(element) {
    return {
        enabled: false,
        strict: false,
        business_days_only: false,
        value: '',
        unit: 'd',
        occurrence: 'first',
        compare_type: 'static',
        compare_table: '',
        compare_attribute: '',
        is_date: false,
        offset_date: '',
        pql: ''
    }
}

/**
 * default value of expression property
 * @param element
 * @returns {{reference_event: string, "compare_value.event_attribute.table": string, scope: string, "event_attribute.attribute": string, "compare_value.event_attribute.attribute": string, "event_attribute.table": string, "compare_value.type": string, "compare_value.is_numeric": boolean, "compare_value.reference_event": string, enabled: boolean, operator: string, "compare_value.value": string}}
 */
export function getDefaultExpression(element) {
    return {
        'enabled': false,
        'label': '',
        'mode': 'static',
        'pql': '',
        'scope': 'trace',
        'reference_event': '',
        'event_attribute.table': '',
        'event_attribute.attribute': '',
        'operator': '=',
        'compare_value.type': 'static',
        'compare_value.is_numeric': false,
        'compare_value.value': '',
        'compare_value.reference_event': '',
        'compare_value.event_attribute.table': '',
        'compare_value.event_attribute.attribute': '',
        'compare_reference_default': false
    }
}

/**
 * default value of roles property
 * @param element
 * @returns {{attribute: string, value: string, table: string}}
 */
export function getDefaultRoles(element) {
    return {
        enabled: false,
        attribute: '',
        value: ''
    }
}

export function getDefaultNestedExpression(element) {
    return {
        type: 'group',
        condition: 'AND',
        expressions: [],
        expression: null
    }
}

export function durationToString(link, editorMode = false, showLabel = true) {
    const {type} = link;
    const durationProp = getProperty(link, 'duration')
    const durationLabel = getProperty(link, 'durationLabel')
    if (!durationProp.enabled) {
        return ''
    }
    if (durationLabel && showLabel && !editorMode) {
        return durationLabel
    }

    const durationToken = type == 'od:Response' ? 'within' : 'after'
    const {compare_type, value, unit, compare_table, compare_attribute, occurrence, is_date, offset_date, pql, business_days_only} = durationProp

    if (compare_type === 'static') {
        const businessPart = business_days_only ? 'b' : '';
        return editorMode ? `${durationToken} ${value}${businessPart}${unit} of ${occurrence} occurrence` : `${value} ${business_days_only ? 'Business ' : ''}${durationUnitToText(unit)}`
    }
    // valid dynamic

    const attribute = !(compare_type === 'pql') ? `"${compare_table}"."${compare_attribute}"` : `[${pql}]`

    // const datePart = 'AS_DATE' + (offset_date ? ` ${offset_date}${unit}` : '')
    const datePart = is_date ? ' DATE ' : ''
    const midPart = is_date ? '' : (business_days_only ? 'b' : '') + unit //(ВОТ тут должна быть проверка флага на бизнес дни)
    return editorMode ?
        `${durationToken} ${datePart} ${attribute} ${midPart} of ${occurrence} occurrence` :
        `? ${durationUnitToText(unit)} (? = ${attribute})`
}

/**
 *
 * @param element Expression element
 * @param editorMode if true, the expression is formatted for the editor
 * @param showLabel if true, the label is shown if present in place of the expression
 */
export function expressionToString(element, editorMode = false, showLabel = true) {
    const expression = getBusinessObject(element);
    const usePql = expression['mode'] === 'pql'
    const label = expression['label']
    if (!editorMode && label && showLabel) {
        return label
    }

    if (usePql && !editorMode) {
        return expression['pql']
    }
    if (usePql && editorMode) {
        return `[${expression['pql']}]`
    }

    let table = expression['event_attribute.table'];
    let attribute = expression['event_attribute.attribute'];
    const compareType = expression['compare_value.type'];
    const compareTable = expression['compare_value.event_attribute.table'];
    const compareAttribute = expression['compare_value.event_attribute.attribute'];
    const isNumeric = expression['compare_value.is_numeric'];
    //these two could be manipulated
    let operator = expression['operator'];
    let compareValue = expression['compare_value.value'];

    const computed = computeCompareValueOperatorString(compareValue, operator, isNumeric)
    compareValue = computed.compareValue;
    operator = computed.operator

    let compareString = ''
    switch (compareType) {
        case 'static':
            compareString = compareValue
            break
        case 'dynamic':
            compareString = `"${compareTable}"."${compareAttribute}"`
            break
        case 'dynamic_events':
            compareString = `\${${expression['compare_value.reference_event']}\}."${compareTable}"."${compareAttribute}"`
            break
    }

    //we use compare value if static compare otherwise the event.table.attr syntax
    return `"${table}"."${attribute}" ${operator} ${compareString}`;
}

/**
 * @param element Link element
 * @param wrapActivities if this is set to true the activity names in expression are represented as "${activity}"
 * @param showLabel if true, the label is shown if present in place of the expression
 * @returns {string}
 */
export function expressionsToString(element, wrapActivities = false, showLabel = true) {
    if (!isLink(element) && !isAny(element, ['od:NestedExpression', 'od:Activity', 'od:NestingActivity', 'od:InitialActivity'])) {
        return '';
    }
    const nested = getProperty(element, 'nestedExpression');
    const condition = getProperty(nested ? nested : element, 'condition');
    const expressions = getProperty(nested ? nested : element, 'expressions');
    if (!expressions || expressions.length === 0) {
        return ''
    }
    let res = expressions.filter(expr => getProperty(expr, 'type')).map(expr => {
        const exprType = getProperty(expr, 'type');

        if (exprType === 'group') {
            return expressionsToString(expr, wrapActivities, showLabel);
        }
        const expression = getProperty(expr, 'expression');
        if (!getProperty(expression, 'enabled')) {
            return ''
        }
        return expressionToString(expression, wrapActivities, showLabel);
    }).filter(e => e.length > 0).join(` ${condition} `);
    if (res === '') {
        return '';
    }
    if (!nested) {
        // in this case it meas we are looking at children so we add brackets
        res = `(${res})`;
    }
    return res;
}

/**
 * returns a map of all present activities in the graph
 * as an object with activity id as key and activity itself as value
 * @param canvas
 * @returns {{}}
 */
export function getActivityMap(canvas, getNesting = true) {
    const root = canvas.getRootElement();
    const children = root.children;
    let activities = {};
    //filter elements that are activities
    _.filter(children, child => isAny(child, ['od:Activity', 'od:NestingActivity'])).forEach(activity => {
        if (is(activity, 'od:Activity') || (is(activity, 'od:NestingActivity') && getNesting)) {
            activities[activity.id] = activity;
        }
        if (activity.children.length > 0) {
            //they may have nested elements
            activity.children.forEach(nested => {
                //and again we filter only the activities
                if (is(nested, 'od:Activity')) {
                    activities[nested.id] = nested;
                }
            })
        }
    });
    return activities;
}

/**
 * return the initial activity of the graph
 * @param canvas
 * @returns {unknown}
 */
export function getInitialActivity(canvas) {
    const root = canvas.getRootElement();
    const children = root.children;
    return _.find(children, child => is(child, 'od:InitialActivity'))
}

/**
 * return a map with all connections present in the graph
 * as an object with id as key and connection itself as value
 * @param canvas
 * @returns {{}}
 */
export function getConnectionMap(canvas) {
    const root = canvas.getRootElement();
    const children = root.children;
    let connections = {};
    _.forEach(children, child => {
        //if an element of the graph is a connection itself, add it
        if (isLink(child)) {
            connections[child.id] = child;
        }
        //if instead is an activity that has elements inside
        if (is(child, 'od:Activity') && child.children.length > 0) {
            child.children.forEach(nested => {
                //we check if we connections in nested elements
                if (isLink(nested)) {
                    connections[nested.id] = nested;
                }
            })
        }
    });
    return connections;
}

/**
 * list of all activities as an array
 * @param canvas
 * @param getInitial if true, the initial activity is added as first element
 * @returns []
 */
export function getActivityList(canvas, getInitial = false, getNesting = true) {
    let res =  Object.values(getActivityMap(canvas, getNesting));
    if (getInitial) {
        const initial = getInitialActivity(canvas)
        if (initial) {
            res.unshift(initial)
        }
    }
    return res
}

/**
 * list of all connections as an array
 * @param canvas
 * @returns []
 */
export function getConnectionList(canvas) {
    return Object.values(getConnectionMap(canvas));
}

/**
 * search in the map the activity with "id", null if not found
 * @param id
 * @param canvas
 * @returns {null}
 */
export function getActivityById(id, canvas) {
    const map = getActivityMap(canvas);
    return map[id] || null;
}

/**
 * search in the map the connection with "id", null if not found
 * @param id
 * @param canvas
 * @returns {null}
 */
export function getConnectionById(id, canvas) {
    const map = getConnectionMap(canvas);
    return map[id] || null;
}

/**
 * e.g. from od:Include to include, od:NoResponse to noResponse
 * @param connType
 * @returns {string}
 */
export function connectionTypeToName(connType) {
    let str = connType.replace('od:', '');
    return str.charAt(0).toLowerCase() + str.slice(1)
}

/**
 * e.g. from include to od:Include
 * @param connName
 * @returns {string}
 */
export function connectionNameToType(connName) {
    return `od:${ucFirst(_.camelCase(connName))}`;
}

/**
 * returns the 'name' property if set, the 'id' otherwise
 * @param act
 * @param escape if true, the name is escaped from html tags
 * @returns {*}
 */
export function getActivityName(act, escape = false) {
    const v = (getBusinessObject(act).name || act.id).replaceAll(/\u00a0/g, ' ');
    return escape ? _.escape(v) : v;
}

export function activitySuggestion(activityNames, filterQuery) {
    return activityNames.sort((a, b) => {
        filterQuery = filterQuery.toLowerCase().replaceAll(/\s/g, '&nbsp;')
        //sort strings by including first the ones that contains the filtered value that user typed
        const aFilt = a.toLowerCase().replaceAll(' ', '&nbsp;').includes(filterQuery)
        const bFilt = b.toLowerCase().replaceAll(' ', '&nbsp;').includes(filterQuery)
        if (aFilt && !bFilt) {
            return -1
        }
        if (bFilt && !aFilt) {
            return 1
        }
        //regular sorting
        return a < b ? -1 : 1
    }).map((item) => {
        const subIdx = item.toLowerCase().replaceAll(/\s/g, '&nbsp;').indexOf(filterQuery.toLowerCase().replaceAll(/\s/g, '&nbsp;'))
        let htmlLabel = item.replaceAll(/\s/g, '&nbsp;')
        //replace in the string the user typed string to surround it with bold tag
        if (subIdx >= 0) {
            const bTag = '<b class="pointer-events-none">'
            htmlLabel = htmlLabel.splice(subIdx, 0, bTag)
            htmlLabel = htmlLabel.splice(subIdx + bTag.length + filterQuery.replaceAll(/\s/g, '&nbsp;').length, 0, '</b>')
        }
        return {
            //the actual label is rendered as html to keep bold style
            label: htmlLabel.replaceAll('&nbsp;', ' '), //replace back the &nbsp; to space to break words,
            item: item
        }
    })
}

/**
 * return either 'delay' or 'deadline' if the element allows a duration,
 * otherwise an empty string
 * @param element
 * @returns {string}
 */
export function getDurationType(element) {
    if (isAny(element, ['od:Condition', 'od:PreCondition'])) {
        return 'delay';
    }
    if (is(element, 'od:Response')) {
        return 'deadline';
    }
    return ''
}

export function checkVisibility(modeler, element) {
    // modeler.get('canvas').zoom('fit-viewport');
    setTimeout(() => {
        const canvas = modeler.get('canvas');
        canvas.zoom();
        // canvas.zoom('fit-viewport')

        //boundaries of the element
        const bounds = modeler.get('elementRegistry').getGraphics(element).getBoundingClientRect();

        const {clientWidth, clientHeight} = canvas.getContainer();

        //250 for the width is due to the presence of the sidebar
        if (bounds.x > 25 && bounds.y > 130 && bounds.bottom < clientHeight && bounds.right < clientWidth - 250) {
            //element is visible in page so we're good
            return;
        }

        //boundary of the element w.r.t. the viewbox
        const elBBox = getBBox(element);
        const currentViewbox = canvas.viewbox();

        //basically we say that the viewbox start position should be where the element is (elBBox)
        //but moved a little bit into the screen (clientWidth,clientHeight)
        canvas.viewbox({
            x: elBBox.x - clientWidth / 3,
            y: elBBox.y - clientHeight / 3,
            width: currentViewbox.width,
            height: currentViewbox.height
        });
    }, 100)

}